import axios from 'axios';
import { API_URL, APIV, TENANT, REQUEST_RESPONSE_ENCRYPTION, SKIP_ENCRYPTION_ROUTE_KEYWORDS } from './../config/constant';
import secureStorage from './../config/encrypt';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
import forge from 'node-forge';

const Axios = axios.create();

let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * Decrypts an encrypted API response.
 */
const decryptResponse = (encryptedResponse) => {
    try {
        const { encrypted_data, encrypted_symmetric_key, iv } = encryptedResponse;

        const responseAesKey = CryptoJS.enc.Utf8.parse(REQUEST_RESPONSE_ENCRYPTION.response_aes_key);

        const zeroIv = CryptoJS.lib.WordArray.create(new Array(4).fill(0));

        const decryptedSymmetricKeyBase64 = CryptoJS.AES.decrypt(
            encrypted_symmetric_key,
            responseAesKey,
            {
                iv: zeroIv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        ).toString(CryptoJS.enc.Utf8);

        const symmetricKey = CryptoJS.enc.Base64.parse(decryptedSymmetricKeyBase64);

        const ivWordArray = CryptoJS.enc.Base64.parse(iv);

        const decryptedData = CryptoJS.AES.decrypt(
            encrypted_data,
            symmetricKey,
            {
                iv: ivWordArray,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        ).toString(CryptoJS.enc.Utf8);

        return JSON.parse(decryptedData);

    } catch (error) {
        console.error('Decryption failed:', error);
        throw new Error('Failed to decrypt response');
    }
};

/**
 * Encrypts request payload for API call.
 */
const encryptPayload = (data) => {
    try {
        const symmetricKey = CryptoJS.lib.WordArray.random(32);
        const iv = CryptoJS.lib.WordArray.random(16);

        const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(data),
            symmetricKey,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        ).toString();

        const publicKey = forge.pki.publicKeyFromPem(REQUEST_RESPONSE_ENCRYPTION.publicKey);
        const symmetricKeyBytes = forge.util.hexToBytes(symmetricKey.toString(CryptoJS.enc.Hex));
        const encryptedSymmetricKey = forge.util.encode64(
            publicKey.encrypt(symmetricKeyBytes, 'RSA-OAEP')
        );

        return {
            encrypted_data: encryptedData,
            encrypted_symmetric_key: encryptedSymmetricKey,
            iv: iv.toString(CryptoJS.enc.Base64)
        };
    } catch (error) {
        console.error("Encryption failed: ", error);
        throw new Error("Failed to encrypt response");
    }
};

/**
 * Decrypts request payload for API call.
 */
const decryptPayload = (payload) => {
    try {
        const { encrypted_data, encrypted_symmetric_key, iv } = payload;

        // PRIVATE KEY required (only for testing) before run this add private key only for testing purpose
        const privateKey = forge.pki.privateKeyFromPem(
            REQUEST_RESPONSE_ENCRYPTION.privateKey
        );

        //decrypt symmetric key (RSA)
        const symmetricKeyBytes = privateKey.decrypt(
            forge.util.decode64(encrypted_symmetric_key),
            'RSA-OAEP'
        );

        // bytes -> hex -> CryptoJS WordArray
        const symmetricKeyHex = forge.util.bytesToHex(symmetricKeyBytes);
        const symmetricKey = CryptoJS.enc.Hex.parse(symmetricKeyHex);

        // IV parse
        const ivWordArray = CryptoJS.enc.Base64.parse(iv);

        // AES decrypt
        const decrypted = CryptoJS.AES.decrypt(
            encrypted_data,
            symmetricKey,
            {
                iv: ivWordArray,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        ).toString(CryptoJS.enc.Utf8);

        return JSON.parse(decrypted);

    } catch (err) {
        console.error("decryptPayload failed:", err);
        return null;
    }
};

/**
 * Encrypts request payload before sending.
 * Skips encryption for whitelisted routes and FormData.
 */
axios.interceptors.request.use(
    (config) => {
        const matchedKeyword = SKIP_ENCRYPTION_ROUTE_KEYWORDS.find(
            (keyword) => config.url?.includes(keyword)
        );

        const shouldSkipEncryption = Boolean(matchedKeyword);
        const isFormData = config.data instanceof FormData;

        console.log({
            url: config.url,
            matchedKeyword,
            shouldSkipEncryption,
            isFormData,
        });

        if (!config.url || shouldSkipEncryption || isFormData) {
            return config;
        }

        config.headers.set('is_encrypt_response', 1);

        if (config.data && Object.keys(config.data).length > 0) {
            config.data = encryptPayload(config.data);

            /** Don't run decrypt code, It's only for testing purpose in local befor enable this add private key */

            // const decryptValue = decryptPayload(config.data);
            // console.log('decryptValue ===============', decryptValue);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    async (response) => {
        if (response.headers["is_encrypt_response"] && +response.headers["is_encrypt_response"] === 1) {
            response.data = decryptResponse(response.data);
        }

        if (response.data && response.data.status != 200) {
            console.error(`${response.data.status} - ${response.data.message}`, response.data)
            // if(response.data.status == 400)
            //     window.commonErrorToast(response.data.message)
            //     toast.error(`${response.data.status} - ${response.data.message}`);
        }
        return response;
    },
    async (error) => {
        // handle error
        if (error.response) {
            if (+error?.response?.headers?.['is_encrypt_response'] === 1 && error?.response?.data) {
				error.response.data = decryptResponse(error.response.data);
			}
            const originalRequest = error?.config;
            console.error(`${error.response.status} - ${error.response.statusText}`, error.response)
            if (error.response.status == 503) {
                // toast.error(`${error.response.status} - ${error.response.statusText}`);
            } else if (error.response.status == 401) {
                // If a token refresh is already in process.
                if (isRefreshing) {
                    // Create a new promise and push it into the failedQueue along with the original request.
                    // We use the original request to later retry this failed request.
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject, originalRequest });
                    })
                        .then((token) => {
                            // Once the token is refreshed, update the Authorization header.
                            originalRequest.headers["Authorization"] = token;
                            // Retry the original request.
                            return Axios(originalRequest);
                        })
                        .catch((err) => {
                            // If token refresh fails, reject the promise.
                            return Promise.reject(err);
                        });
                }
                // Flag to indicate that this request has been retried.
                originalRequest._retry = true;
                // Flag to indicate a token refresh is in progress.
                isRefreshing = true;
                // Object to store the new refresh token.
                let refresh_token_data = {};
                try {
                    // Attempt to refresh the token.
                    refresh_token_data = await refreshToken();

                    // Process the queue of failed requests.
                    processQueue(null, refresh_token_data.token);

                    // Update the Authorization header with the new token.
                    originalRequest.headers["Authorization"] = refresh_token_data.token;

                    // Retry the original request with the new token.
                    return Axios(originalRequest);

                } catch (err) {
                    // If token refresh fails, clear the queue and redirect to logout.
                    processQueue(err, null);
                    window.location.href = "/logout";
                    return Promise.reject(err);
                } finally {
                    // Reset the flag to indicate that the token refresh has completed.
                    isRefreshing = false;
                }
            }
        }

        return error.response;
    });

window.commonErrorToast = function (error) {
    if(typeof error == "string") toast.error(error);
    else if(error.message) toast.error(error.message);
}
// export default {//need to remove
//     getMasters(list) {
//         return this.get("/core/commonservice/master", { master: list });
//     },
//     getToken() {
//         let fetchHeader = (secureStorage.getItem('loginUserInfo'))
//             ? secureStorage.getItem('loginUserInfo')
//             : {};
//         return (fetchHeader && fetchHeader.data && fetchHeader.data.token)
//             ? fetchHeader.data.token : null;
//     },
//     delete(URL, body, headers) {
//         headers = headers || {};
//         if(APIV) headers['apiv'] = APIV;
//         headers['Authorization'] = this.getToken();
//         headers['Accept-Language'] = secureStorage.getItem('lang');
//         URL = (URL[0] == '/') ? URL.substr(1) : URL;
//         return axios.delete(`${API_URL.GETWAY_API}${URL}`, body, {headers})
//     },
//     post(URL, body, headers) {
//         headers = headers || {};
//         if(APIV) headers['apiv'] = APIV;
//         headers['Authorization'] = this.getToken();
//         headers['Accept-Language'] = secureStorage.getItem('lang');
//         URL = (URL[0] == '/') ? URL.substr(1) : URL;
//         return axios.post(`${API_URL.GETWAY_API}${URL}`, body, {headers})
//     },
//     put(URL, body, headers) {
//         headers = headers || {};
//         if(APIV) headers['apiv'] = APIV;
//         headers['Authorization'] = this.getToken();
//         headers['Accept-Language'] = secureStorage.getItem('lang');
//         URL = (URL[0] == '/') ? URL.substr(1) : URL;
//         return axios.put(`${API_URL.GETWAY_API}${URL}`, body, {headers})
//     },
//     get(URL, query, headers) {
//         headers = headers || {};
//         if(APIV) headers['apiv'] = APIV;
//         headers['Authorization'] = this.getToken();
//         headers['Accept-Language'] = secureStorage.getItem('lang');
//         URL = (URL[0] == '/') ? URL.substr(1) : URL;
//         return axios.get(`${API_URL.GETWAY_API}${URL}`,
//             { params: query, headers })
//     }
// }
const MasterapiService = {
    getMasters(list) {
        return this.get("/core/commonservice/master", { master: list });
    },
    getToken() {
        let fetchHeader = (secureStorage.getItem('loginUserInfo'))
            ? secureStorage.getItem('loginUserInfo')
            : {};
        return (fetchHeader && fetchHeader.data && fetchHeader.data.token)
            ? fetchHeader.data.token : null;
    },
    delete(URL, body, headers) {
        headers = headers || {};
        if(APIV) headers['apiv'] = APIV;
        headers['Authorization'] = this.getToken();
        headers['Accept-Language'] = secureStorage.getItem('lang');
        headers['tenant'] = TENANT;
        URL = (URL[0] == '/') ? URL.substr(1) : URL;
        return axios.delete(`${API_URL.GETWAY_API}${URL}`, body, {headers})
    },
    post(URL, body, headers) {
        headers = headers || {};
        if(APIV) headers['apiv'] = APIV;
        headers['Authorization'] = this.getToken();
        headers['Accept-Language'] = secureStorage.getItem('lang');
        headers['tenant'] = TENANT;
        URL = (URL[0] == '/') ? URL.substr(1) : URL;
        return axios.post(`${API_URL.GETWAY_API}${URL}`, body, {headers})
    },
    put(URL, body, headers) {
        headers = headers || {};
        if(APIV) headers['apiv'] = APIV;
        headers['Authorization'] = this.getToken();
        headers['Accept-Language'] = secureStorage.getItem('lang');
        headers['tenant'] = TENANT;
        URL = (URL[0] == '/') ? URL.substr(1) : URL;
        return axios.put(`${API_URL.GETWAY_API}${URL}`, body, {headers})
    },
    get(URL, query, headers) {
        headers = headers || {};
        if(APIV) headers['apiv'] = APIV;
        headers['Authorization'] = this.getToken();
        headers['Accept-Language'] = secureStorage.getItem('lang');
        headers['tenant'] = TENANT;
        URL = (URL[0] == '/') ? URL.substr(1) : URL;
        return axios.get(`${API_URL.GETWAY_API}${URL}`,
            { params: query, headers })
    },
    download(URL, body, responseType,  headers) {
        headers = headers || {};       
        if (APIV) headers['apiv'] = APIV;
        headers['Authorization'] = this.getToken();
        headers['Accept-Language'] = secureStorage.getItem('lang') || 'en';
        //headers['apikey'] = API_URL.API_KEY;        
        URL = (URL[0] === '/') ? URL.substr(1) : URL;        
        return Axios.post(`${API_URL.GETWAY_API}${URL}`,  body, {responseType, headers,withCredentials:false })
    },
}

async function refreshToken() {
    try {
        let loginUserInfo = (secureStorage.getItem('loginUserInfo'))
            ? secureStorage.getItem('loginUserInfo')
            : null;
        if (loginUserInfo) {
            let url = `${API_URL.GETWAY_API}account/user/refresh_token`;
            const { data } = await Axios.post(url, { refresh_token: loginUserInfo.data.refresh_token }, {
                headers: { 'Authorization': loginUserInfo.data.token }
            });
            if (data && data.status === 200) {
                secureStorage.setItem('loginUserInfo', data);
                return data.data;
            } else {
                window.location.href = '/logout';
            }
        } else {
            window.location.href = '/logout';
        }

    } catch (error) {
        console.error("Error refreshing token: ", error);
        //  window.location.href = '/logout';
        return null;
    }
}

export default MasterapiService;